import React from 'react';
import {
  Typography,
  Box,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Button,
  Chip
} from '@mui/material';
import { AppConfigurations } from '@/types/company-config';

interface AppConfigurationsTabProps {
  data: AppConfigurations;
  onChange: (field: string, value: unknown) => void;
  readOnly?: boolean;
}

const AppConfigurationsTab: React.FC<AppConfigurationsTabProps> = ({ data, onChange, readOnly = false }) => {
  // Mock app data - in a real implementation, this would come from the apps service
  const mockApps = [
    {
      id: 'credit-check-app',
      name: 'Credit Check Integration',
      description: 'Integration with credit reporting agencies',
      category: 'Financial Services',
      installed: true
    },
    {
      id: 'document-processor',
      name: 'Document Processor',
      description: 'Automated document processing and OCR',
      category: 'Automation',
      installed: true
    },
    {
      id: 'email-templates',
      name: 'Email Template Manager',
      description: 'Customizable email templates for client communication',
      category: 'Communication',
      installed: false
    },
    {
      id: 'analytics-dashboard',
      name: 'Advanced Analytics',
      description: 'Enhanced reporting and analytics capabilities',
      category: 'Analytics',
      installed: true
    }
  ];

  const handleAppToggle = (appId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(appId, {
      ...data[appId],
      enabled: event.target.checked
    });
  };

  const getAppConfig = (appId: string) => {
    return data[appId] || { enabled: true, settings: {} };
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        App Configurations
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage settings for installed applications. Each app can be enabled or disabled, 
        and may have additional configuration options.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        App configurations allow you to control which installed applications are active 
        and how they behave across your platform. Changes here will affect all users.
      </Alert>

      {mockApps.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Apps Installed
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Install apps from the marketplace to configure them here.
          </Typography>
          <Button variant="contained" color="primary">
            Visit App Marketplace
          </Button>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Application</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Enabled</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mockApps.map((app) => {
                const config = getAppConfig(app.id);
                return (
                  <TableRow key={app.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {app.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {app.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={app.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={app.installed ? 'Installed' : 'Not Installed'} 
                        color={app.installed ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {app.installed ? (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.enabled ?? true}
                              onChange={handleAppToggle(app.id)}
                              disabled={readOnly}
                              size="small"
                            />
                          }
                          label=""
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {app.installed ? (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={readOnly}
                        >
                          Configure
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={readOnly}
                        >
                          Install
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box mt={3}>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> Disabling an app will prevent it from running but won't uninstall it. 
          Users will not be able to access disabled apps. App-specific data and configurations 
          will be preserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default AppConfigurationsTab;